// Loads bundled, read-only content and converts it to app types.
// To add a country cookbook: drop a JSON file in country-cookbooks/ and list it below.
import catalogJson from './catalog.json';
import italy from './country-cookbooks/italy.json';
import japan from './country-cookbooks/japan.json';
import { fromNumber } from '../src/logic/quantity';
import type { SeedUnitKey } from '../src/logic/units';
import type { HolidayId } from '../src/logic/holidays';
import type { Diet, Ingredient, IngredientAisle, IngredientLine, Recipe, Step, Utensil, UtensilAisle } from '../src/types';

type RawLine = { ingredient: string; amount: number; unit: string | null };
type RawUtensil = { utensil: string; amount: number; scaleEvery?: number };
type RawStep = { title: string; explainer?: string; uses?: string[]; timers?: { label: string; minutes: number }[] };
type RawRecipe = {
  id: string; title: string; explainer: string; servings?: number; durationMin?: number; notDiet?: string[];
  ingredients: RawLine[]; utensils: RawUtensil[]; steps: RawStep[];
};
type RawCookbook = {
  id: string; country: string; flag: string; title: string; explainer: string; premium: boolean;
  sections: { title: string; note: string; holiday?: string; recipes: RawRecipe[] }[];
};

export type CountryCookbook = {
  id: string;
  country: string;
  flag: string;
  title: string;
  explainer: string;
  premium: boolean;
  sections: { title: string; note: string; holiday?: HolidayId; recipeIds: string[] }[];
};

const RAW_COOKBOOKS = [italy, japan] as RawCookbook[];

const toLine = (l: RawLine): IngredientLine => ({
  ingredientId: l.ingredient,
  amount: fromNumber(l.amount),
  unit: l.unit ? { seed: l.unit as SeedUnitKey } : null,
});

function toRecipe(r: RawRecipe): Recipe {
  const byIngredient = new Map(r.ingredients.map(l => [l.ingredient, toLine(l)]));
  const steps: Step[] = r.steps.map((s, i) => ({
    id: `${r.id}#${i + 1}`,
    title: s.title,
    explainer: s.explainer ?? '',
    ingredients: (s.uses ?? []).map(u => byIngredient.get(u)).filter((x): x is IngredientLine => !!x),
    utensils: [],
    timers: (s.timers ?? []).map(t => ({ label: t.label, seconds: Math.round(t.minutes * 60) })),
  }));
  return {
    id: r.id,
    title: r.title,
    explainer: r.explainer,
    servings: r.servings ?? 1,
    durationMin: r.durationMin ?? 0,
    priceCents: 0,
    ingredients: r.ingredients.map(toLine),
    utensils: r.utensils.map(u => ({ utensilId: u.utensil, amount: u.amount, scaleEvery: u.scaleEvery ?? null })),
    steps,
    ...(r.notDiet?.length ? { notDiet: r.notDiet as Diet[] } : {}),
    updatedAt: 0,
  };
}

export const COUNTRY_COOKBOOKS: CountryCookbook[] = RAW_COOKBOOKS.map(c => ({
  id: c.id,
  country: c.country,
  flag: c.flag,
  title: c.title,
  explainer: c.explainer,
  premium: c.premium,
  sections: c.sections.map(s => ({ title: s.title, note: s.note, ...(s.holiday ? { holiday: s.holiday as HolidayId } : {}), recipeIds: s.recipes.map(r => r.id) })),
}));

export const CONTENT_RECIPES: Record<string, Recipe> = Object.fromEntries(
  RAW_COOKBOOKS.flatMap(c => c.sections.flatMap(s => s.recipes)).map(r => [r.id, toRecipe(r)]),
);

/** Recipes from cookbook sections made for a holiday, with the section they come from. */
export function holidayRecipes(id: HolidayId): { recipeId: string; cookbook: CountryCookbook; section: string }[] {
  return COUNTRY_COOKBOOKS.flatMap(cookbook =>
    cookbook.sections.filter(s => s.holiday === id).flatMap(s => s.recipeIds.map(recipeId => ({ recipeId, cookbook, section: s.title }))),
  );
}

/** Which country cookbook a content recipe belongs to. */
export function cookbookOf(id: string): CountryCookbook | undefined {
  return COUNTRY_COOKBOOKS.find(c => id.startsWith(`${c.id}/`));
}

export const CATALOG_INGREDIENTS: Record<string, Ingredient> = Object.fromEntries(
  catalogJson.ingredients.map(i => [
    i.id,
    {
      id: i.id, title: i.title, aisle: i.aisle as IngredientAisle, notDiet: i.notDiet as Diet[], synonyms: i.synonyms,
      explainer: '', builtIn: true,
    },
  ]),
);

export const CATALOG_UTENSILS: Record<string, Utensil> = Object.fromEntries(
  catalogJson.utensils.map(u => [u.id, { id: u.id, title: u.title, aisle: u.aisle as UtensilAisle, synonyms: u.synonyms, explainer: '', builtIn: true }]),
);

export const RAW_CONTENT = { catalog: catalogJson, cookbooks: RAW_COOKBOOKS };
